// src/pages/BlogPostPage.tsx
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getBlogPostBySlug, BlogPost } from "@/modules/blog/blogService";
import EmailCapture from "@/components/EmailCapture";
import "../styles/BlogPostPage.css";
import { stateImages } from "./Blog"; // map of state → image array

const FALLBACK_IMAGE = "/images-webp/blog-fallback-solar.webp";

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  /* ---------- helper: choose deterministic hero image ---------- */
  const getStateImage = (state: string, postId: string): string => {
    const array =
      stateImages[state.trim() as keyof typeof stateImages] ??
      stateImages.default;

    if (!array?.length) return FALLBACK_IMAGE;

    const idx =
      postId.split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0) %
      array.length;

    return array[idx] ?? FALLBACK_IMAGE;
  };

  /* ---------- fetch post by slug ---------- */
  useEffect(() => {
    if (!slug) return;

    (async () => {
      try {
        const data = await getBlogPostBySlug(slug);
        setPost(data);
      } catch (err) {
        console.error("Error loading blog post:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  /* ---------- loading / 404 UI ---------- */
  if (loading) return <p>Loading…</p>;

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <title>Post Not Found | Your Energy Guru</title>
        <meta name="robots" content="noindex" />
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <p>
            Blog post not found. <Link to="/blog">Browse all articles</Link>.
          </p>
        </div>
      </div>
    );
  }

  /* ---------- derived values ---------- */
  const readableDate = new Date(post.datePublished).toLocaleDateString(
    "en-US",
    { year: "numeric", month: "long", day: "numeric" }
  );

  const postImage =
    post.image?.trim() ||
    getStateImage(post.state ?? "default", post.id) ||
    FALLBACK_IMAGE;

  /* ---------- JSON-LD ---------- */
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    image: postImage,
    keywords: post.keywords?.join(", "),
    author: { "@type": "Person", name: post.author || "Your Energy Guru" },
    publisher: {
      "@type": "Organization",
      name: "Your Energy Guru",
      logo: {
        "@type": "ImageObject",
        url: "https://yourenergyguru.com/logo.png",
      },
    },
    datePublished: new Date(post.datePublished).toISOString(),
    description: post.description,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://yourenergyguru.com/blog/${post.slug}`,
    },
  };

  /* ---------- render ---------- */
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="blog-post-container">
          <article className="blog-post">
            {/* header */}
            <div className="blog-header">
              <h1 className="blog-title">{post.title}</h1>
              <p className="blog-meta">
                <em>
                  By {post.author || "Your Energy Guru"} | {readableDate}
                </em>
              </p>
              <img
                src={postImage}
                alt={`${post.title} – featured image`}
                className="blog-image"
                onError={(e) => {
                  const img = e.currentTarget;
                  if (img.dataset.fallbackApplied) return;
                  img.dataset.fallbackApplied = "1";
                  img.src = FALLBACK_IMAGE;
                }}
              />
            </div>

            {/* content */}
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            <EmailCapture
              list="fl-solar-alerts"
              source={`blog:${post.slug}`}
              title="Solar math changes when utilities move. Know first."
              blurb="FPL rate filings, net-metering changes, hurricane-season prep — one short email when something actually affects whether solar pays in Florida."
              buttonLabel="Keep me posted"
            />

            {/* author box */}
            <div className="author-box">
              <img
                src="/images-webp/professionaljesse.webp"
                alt="Jesse Garlick headshot"
                className="author-image"
              />
              <h3>About Jesse Garlick</h3>
              <p>
                Jesse Garlick is the founder of Energy Guru, where he helps
                homeowners cut through solar-industry hype and make clear,
                numbers-based decisions about home energy.
                {/* TODO Jesse: add 1–2 sentences of your real background here
                    (years researching solar, your own experience going solar,
                    relevant work/expertise) — real specifics strengthen E-E-A-T. */}
              </p>
              <p>
                Every article is researched against primary sources — utility
                rate filings, IRS guidance, and state energy programs — and
                reviewed for accuracy before it's published.
              </p>
              <p className="author-link">
                <Link to="/about-jesse-garlick">Learn more about Jesse →</Link>
              </p>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}
